import { Link, useParams } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../api/client';
import { useHistory, useReceipt } from '../api/hooks';
import type { Receipt, SessionView } from '../api/types';
import { TopBar } from '../components/chrome';
import { Cta, ErrorState, Loading, Muted, Screen, Title } from '../components/ui';
import { cardLabel, durationLabel, lotAddressLabel, receiptLineLabel, usd } from '../lib/format';
import {
  isSameLotDay,
  lotClockLabel,
  lotDateLabel,
  lotISODate,
  lotSpanLabel,
  resolveLotTimeLanguage,
} from '../lib/lot-time';

/** "Today" and dates follow the LOT's calendar, not the device's (review §5). */
const dayLabel = (iso: string, timezone: string, today: string) =>
  isSameLotDay(iso, new Date(), timezone) ? today : lotDateLabel(iso, timezone);

type HistoryView = 'inProgress' | 'past';

const isInProgress = (session: SessionView) => session.status === 'active' || session.status === 'pending';

/** Time-descending, with lot, plate, span and amount actually paid (FR-U-44). */
export function HistoryPage() {
  const { t } = useTranslation();
  const history = useHistory();

  if (!tokens.signedIn) {
    return (
      <>
        <TopBar title={t('history.title')} />
        <Screen>
          <Title sub={t('history.signInBody')}>{t('history.signInTitle')}</Title>
          <Link to="/login" search={{ next: undefined }} className="w-full">
            <Cta label={t('history.signIn')} />
          </Link>
        </Screen>
      </>
    );
  }

  if (history.isPending) {
    return (
      <>
        <TopBar title={t('history.title')} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (history.isError) {
    return (
      <>
        <TopBar title={t('history.title')} />
        <Screen>
          <ErrorState message={t('history.loadFailed')} onRetry={() => void history.refetch()} />
        </Screen>
      </>
    );
  }

  return <HistoryList sessions={history.data.sessions} />;
}

/**
 * Active and finished stays are separate segments, not one mixed column with a
 * status footnote (review §4.10). Filters are client-side over the fetched
 * list — the history endpoint takes no query parameters.
 */
function HistoryList({ sessions }: { sessions: SessionView[] }) {
  const { t } = useTranslation();
  // Opens on whichever segment has something to show; a driver with a car
  // parked right now came for that session.
  const [view, setView] = useState<HistoryView>(() => (sessions.some(isInProgress) ? 'inProgress' : 'past'));
  const [plateFilter, setPlateFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const inProgress = sessions.filter(isInProgress);
  const past = sessions.filter((session) => !isInProgress(session));
  const plates = [...new Set(past.map((session) => session.plate))];

  const visible =
    view === 'inProgress'
      ? inProgress
      : past.filter(
          (session) =>
            (!plateFilter || session.plate === plateFilter) &&
            // The picked calendar date is compared on each session's LOT day.
            (!dateFilter || lotISODate(session.startedAt, session.lot.timezone) === dateFilter),
        );

  const emptyMessage =
    view === 'inProgress'
      ? t('history.emptyInProgress')
      : past.length === 0
        ? t('history.empty')
        : t('history.noMatches');

  return (
    <>
      <TopBar title={t('history.title')} />
      <Screen>
        {/* The top bar already carries the page title — no body repeat
            (review §5); the segments are the page's first content. */}
        <div className="grid grid-cols-2 rounded-lg bg-surface-muted p-1">
          {(['inProgress', 'past'] as const).map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={view === item}
              onClick={() => setView(item)}
              className={`min-h-11 rounded-md text-sm font-medium ${view === item ? 'bg-white text-ink' : 'text-muted'}`}
            >
              {t(`history.${item}`)}
            </button>
          ))}
        </div>

        {view === 'past' && past.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            <select
              aria-label={t('history.plateFilter')}
              value={plateFilter}
              onChange={(event) => setPlateFilter(event.target.value)}
              className="min-h-11 rounded-lg border border-hairline bg-transparent px-3 text-sm"
            >
              <option value="">{t('history.allPlates')}</option>
              {plates.map((plate) => (
                <option key={plate} value={plate}>
                  {plate}
                </option>
              ))}
            </select>
            <input
              type="date"
              aria-label={t('history.dateFilter')}
              value={dateFilter}
              onChange={(event) => setDateFilter(event.target.value)}
              className="num min-h-11 rounded-lg border border-hairline bg-transparent px-3 text-sm"
            />
          </div>
        ) : null}

        {visible.length === 0 ? (
          <Muted>{emptyMessage}</Muted>
        ) : (
          <div>
            {visible.map((session) => (
              <Link
                key={session.id}
                to="/receipt/$sessionId"
                params={{ sessionId: session.id }}
                className="flex items-center gap-3 border-b border-hairline py-3.5"
              >
                <span className="flex flex-1 flex-col gap-1">
                  <span className="text-base font-semibold">{session.lot.name}</span>
                  <span className="num text-xs text-muted">
                    {[
                      session.plate,
                      session.spaceNumber !== null
                        ? t('history.spaceNumber', { number: session.spaceNumber })
                        : null,
                      dayLabel(session.startedAt, session.lot.timezone, t('history.today')),
                      durationLabel(session.totalMinutes, t),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="num text-[17px] font-semibold">{usd(session.paidTotalCents)}</span>
              </Link>
            ))}
          </div>
        )}
      </Screen>
    </>
  );
}

/**
 * The itemised receipt (FR-U-45): the first payment and every extension as
 * separate lines followed by the final amount paid. This is what gets filed
 * for expenses, so it has to reconcile exactly — and it carries
 * the formal fields (receipt number, lot address, tender, refunds) whenever the
 * API provides them. Every one of those fields is optional: against an API
 * predating backend de90284 the receipt simply omits them.
 */
export function ReceiptPage() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams({ from: '/receipt/$sessionId' });
  const receipt = useReceipt(sessionId);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);

  if (receipt.isPending) {
    return (
      <>
        <TopBar title={t('receipt.title')} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (receipt.isError || !receipt.data) {
    return (
      <>
        <TopBar title={t('receipt.title')} />
        <Screen>
          <ErrorState message={t('receipt.loadFailed')} onRetry={() => void receipt.refetch()} />
        </Screen>
      </>
    );
  }

  const data = receipt.data;
  const lang = resolveLotTimeLanguage(i18n.language);
  const timezone = data.session.lot.timezone;
  const exists = i18n.exists.bind(i18n);

  const address = data.lotAddress ? lotAddressLabel(data.lotAddress) : null;
  const lineCard = (line: Receipt['lines'][number]) => cardLabel(line.cardBrand, line.cardLast4, t, exists);
  // Status text comes from the code via the bundle; the amount only renders
  // once something has actually been returned (refundedCents is succeeded-only).
  const lineRefund = (line: Receipt['lines'][number]) => {
    if (!line.refundStatus) return null;
    const label = t(`receipt.refund.${line.refundStatus}`);
    return line.refundedCents ? `${label} · ${usd(line.refundedCents)}` : label;
  };

  const asText = [
    t('receipt.subject'),
    data.receiptNumber ? t('receipt.number', { number: data.receiptNumber }) : '',
    [
      data.session.lot.name,
      data.session.plate,
      data.session.spaceNumber !== null
        ? t('receipt.spaceNumber', { number: data.session.spaceNumber })
        : null,
    ]
      .filter(Boolean)
      .join(' · '),
    address ?? '',
    lotSpanLabel(data.session.startedAt, data.session.expiresAt, timezone, lang),
    ...data.lines.flatMap((line) => {
      const card = lineCard(line);
      const refund = lineRefund(line);
      return [
        `${receiptLineLabel(line, t)}  ${usd(line.amountCents)}${card ? `  ${card}` : ''}`,
        refund ?? '',
      ];
    }),
    `${t('receipt.total')} ${usd(data.paidTotalCents)}`,
  ]
    .filter(Boolean)
    .join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
    } catch {
      // Clipboard can be denied (permissions, insecure context); no false
      // "copied" feedback in that case.
      return;
    }
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <TopBar title={t('receipt.title')} />
      <Screen>
        {/* The top bar already says "Receipt"; the body opens with the
            receipt's own facts instead of repeating it (review §5). */}
        <div className="flex flex-col gap-1">
          {data.receiptNumber ? (
            <span className="num text-[13px] text-muted">
              {t('receipt.number', { number: data.receiptNumber })}
            </span>
          ) : null}
          <Muted>
            {[
              data.session.lot.name,
              data.session.plate,
              data.session.spaceNumber !== null
                ? t('receipt.spaceNumber', { number: data.session.spaceNumber })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Muted>
          {address ? <Muted>{address}</Muted> : null}
          <span className="num text-[13px] text-muted">
            {lotSpanLabel(data.session.startedAt, data.session.expiresAt, timezone, lang)}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-hairline">
          {data.lines.map((line) => {
            const card = lineCard(line);
            const refund = lineRefund(line);
            return (
              <div key={line.id} className="flex items-center gap-3 border-b border-hairline p-3.5">
                <span className="flex flex-1 flex-col gap-0.5">
                  <span className="text-[15px] font-semibold">{receiptLineLabel(line, t)}</span>
                  <span className="text-xs text-muted">
                    {lotClockLabel(line.occurredAt, timezone, lang)}
                    {card ? ` · ${card}` : ''}
                  </span>
                  {refund ? <span className="text-xs font-medium text-warning">{refund}</span> : null}
                </span>
                <span className="num text-base">{usd(line.amountCents)}</span>
              </div>
            );
          })}

          <div className="flex justify-between bg-surface-muted p-3.5">
            <span className="num text-[15px] font-bold">{t('receipt.total')}</span>
            <span className="num text-[15px] font-bold">{usd(data.paidTotalCents)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => void copy()}
            aria-live="polite"
            className={`min-h-11 rounded-lg border-[1.5px] px-3 text-sm font-semibold ${
              copied ? 'border-success text-success' : 'border-hairline'
            }`}
          >
            {copied ? t('receipt.copied') : t('receipt.copy')}
          </button>
          <a
            href={`mailto:?subject=${encodeURIComponent(t('receipt.subject'))}&body=${encodeURIComponent(asText)}`}
            className="flex min-h-11 items-center justify-center rounded-lg border-[1.5px] border-hairline px-3 text-center text-sm font-semibold"
          >
            {t('receipt.email')}
          </a>
        </div>

        <Muted>{t('receipt.note')}</Muted>
      </Screen>
    </>
  );
}
